import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Switch } from 'antd';
import { formatters } from 'services';
import {
  HISTORICAL_SCENARIO,
  selectors,
  toggleAFSBondSwitchover,
  toggleInvestmentRationale,
  togglePayCoupons,
  toggleSimplifiedOptionWriting,
  toggleSound,
  updateBlueprintNewsImportances,
  updateFrequency,
  updateHistoricalNewsDelivery,
  updateHistoricalStep,
  updateSessionDv01Limits,
} from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import Field from '../../SessionSettingsShared/components/Field';
import { isSessionLocked } from '../../SessionSettingsShared/helpers';
import InlineNumber from './InlineNumber';
import NullableInlineNumber from './NullableInlineNumber';
import './inline_edit.css';

/**
 * Renders live session behavior settings, including the player-facing option
 * writing policy, and dispatches each change immediately to the session API.
 * Period duration commits on blur/Enter; frequency remains a read-only pill.
 */
const GeneralCardV2: React.FC<{ blueprintMode?: boolean }> = ({
  blueprintMode,
}) => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const currencies = useSelector(selectors.currencies);
  const reportingCurrencySymbol =
    currencies?.[session.reporting_currency]?.symbol;
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;
  // Fast playback owns its daily/news clock; predetermined dates also ignore frequency.
  const showFrequency =
    session.execution_engine !== 'fast' &&
    session.time_advancement_type !== 'predetermined_intervals';
  const staggeredNewsEnabled =
    session.historical_news_delivery_enabled ?? false;
  const newsPushInterval = session.historical_news_push_interval_seconds ?? 30;
  const newsQuietSeconds = session.historical_news_quiet_seconds ?? 0;
  const settingsLocked = isSessionLocked(session);

  const updateNewsDelivery = (
    overrides: Partial<{
      historical_news_delivery_enabled: boolean;
      historical_news_push_interval_seconds: number;
      historical_news_quiet_seconds: number;
    }>,
  ) =>
    dispatch(
      updateHistoricalNewsDelivery({
        session_id: session.id,
        historical_news_delivery_enabled: staggeredNewsEnabled,
        historical_news_push_interval_seconds: newsPushInterval,
        historical_news_quiet_seconds: newsQuietSeconds,
        ...overrides,
      }),
    );

  // Blueprint authoring only: comma-separated news-importance levels (e.g. "1,2,3"), committed on
  // blur/Enter and re-synced from the saved value.
  const [newsText, setNewsText] = useState(
    (session.news_importances ?? []).join(','),
  );
  useEffect(() => {
    setNewsText((session.news_importances ?? []).join(','));
  }, [session.news_importances]);

  const commitNewsImportances = () => {
    const levels = newsText
      .split(',')
      .map(x => parseInt(x.trim(), 10))
      .filter(n => Number.isInteger(n) && n > 0);
    const current = session.news_importances ?? [];
    if (levels.join(',') !== current.join(',')) {
      dispatch(
        updateBlueprintNewsImportances({
          id: session.id,
          news_importances: levels,
        }),
      );
    }
  };

  return (
    <>
      {/* Only legacy historical sessions use these pacing controls. */}
      {isHistorical && session.execution_engine !== 'fast' && (
        <SettingsCard title="Pacing & Cadence" className="set3b-pacing-card">
          <Field
            label="Period duration"
            hint="Wall-clock seconds the engine waits before advancing one historical period."
          >
            <InlineNumber
              value={session.historical_sleep_time}
              min={1}
              plain
              addonAfter="s"
              onCommit={v =>
                dispatch(
                  updateHistoricalStep({
                    session_id: session.id,
                    sleep_time: v,
                    admin: false,
                  }),
                )
              }
              ariaLabel="Period duration"
            />
          </Field>
          <Field
            label="Stagger news delivery"
            hint="Release this period's existing news in balanced pushes. Changes apply from the next period."
          >
            <Switch
              checked={staggeredNewsEnabled}
              disabled={settingsLocked}
              onChange={historical_news_delivery_enabled =>
                updateNewsDelivery({ historical_news_delivery_enabled })
              }
            />
          </Field>
          {staggeredNewsEnabled && (
            <>
              <Field
                label="Seconds between news pushes"
                hint="The period's news is divided as evenly as possible across this cadence."
              >
                <InlineNumber
                  value={newsPushInterval}
                  min={2}
                  plain
                  addonAfter="s"
                  disabled={settingsLocked}
                  onCommit={historical_news_push_interval_seconds =>
                    updateNewsDelivery({
                      historical_news_push_interval_seconds,
                    })
                  }
                  ariaLabel="Seconds between news pushes"
                />
              </Field>
              <Field
                label="News-free time"
                hint="No new stories arrive during these final seconds before the next period."
              >
                <InlineNumber
                  value={newsQuietSeconds}
                  min={0}
                  max={Math.max(0, session.historical_sleep_time - 1)}
                  plain
                  addonAfter="s"
                  disabled={settingsLocked}
                  onCommit={historical_news_quiet_seconds =>
                    updateNewsDelivery({ historical_news_quiet_seconds })
                  }
                  ariaLabel="News-free time before next period"
                />
              </Field>
            </>
          )}
          {showFrequency && (
            <Field
              label="Frequency"
              hint="Simulated days advanced each period."
            >
              <InlineNumber
                value={session.frequency}
                min={1}
                max={31}
                plain
                // Reserve the trailing "s" width always (visibility:hidden
                // keeps layout space) so day↔days never resizes the addon and
                // can't shift the tile. Same trick as Futures' contracts term.
                addonAfter={v => (
                  <>
                    day
                    <span
                      style={{ visibility: v === 1 ? 'hidden' : 'visible' }}
                    >
                      s
                    </span>
                  </>
                )}
                disabled={settingsLocked}
                onCommit={v =>
                  dispatch(
                    updateFrequency({ session_id: session.id, frequency: v }),
                  )
                }
                ariaLabel="Frequency"
              />
            </Field>
          )}
        </SettingsCard>
      )}

      <SettingsCard title="DV01 Reference">
        <Field
          label="Net DV01 limit"
          hint="Reference shown to players alongside their net bond DV01; it is not enforced by trading or risk mechanics."
        >
          <NullableInlineNumber
            value={session.net_dv01_limit}
            min={0}
            ariaLabel="Net DV01 limit"
            formatter={value =>
              value == null || value === ''
                ? ''
                : formatters.commas2Digits(value)
            }
            suffix={reportingCurrencySymbol}
            onCommit={net_dv01_limit =>
              dispatch(
                updateSessionDv01Limits({
                  session_id: session.id,
                  net_dv01_limit,
                }),
              )
            }
          />
        </Field>
        <Field
          label="Gross DV01 limit"
          hint="Reference shown to players alongside their gross bond DV01; it is not enforced by trading or risk mechanics."
        >
          <NullableInlineNumber
            value={session.gross_dv01_limit}
            min={0}
            ariaLabel="Gross DV01 limit"
            formatter={value =>
              value == null || value === ''
                ? ''
                : formatters.commas2Digits(value)
            }
            suffix={reportingCurrencySymbol}
            onCommit={gross_dv01_limit =>
              dispatch(
                updateSessionDv01Limits({
                  session_id: session.id,
                  gross_dv01_limit,
                }),
              )
            }
          />
        </Field>
      </SettingsCard>

      <SettingsCard title="Behaviour">
        <Field
          label="Require investment rationale"
          hint="Force players to write a rationale on every trade."
        >
          <Switch
            checked={session.require_investment_rationale}
            onChange={e =>
              dispatch(
                toggleInvestmentRationale({ require_investment_rationale: e }),
              )
            }
          />
        </Field>
        <Field label="Allow sound" hint="Enable UI sound effects for players.">
          <Switch
            checked={session.allow_sound}
            onChange={e => dispatch(toggleSound({ allow_sound: e }))}
          />
        </Field>
        {session.allow_simplified_option_writing !== undefined && (
          <Field
            label="Allow option writing"
            hint="Allow players to write short options and sell or reduce long option positions."
          >
            <Switch
              checked={session.allow_simplified_option_writing}
              onChange={allow_simplified_option_writing =>
                dispatch(
                  toggleSimplifiedOptionWriting({
                    allow_simplified_option_writing,
                  }),
                )
              }
            />
          </Field>
        )}
        {(isHistorical || session.live_synthetic_bonds_enabled) && (
          <Field
            label="Rollover bonds"
            hint="When a replacement issue becomes available, close each player's old AFS bond position and reopen the same quantity in the matching new issue."
          >
            <Switch
              checked={session.afs_bond_switchover_enabled}
              disabled={settingsLocked}
              onChange={afs_bond_switchover_enabled =>
                dispatch(
                  toggleAFSBondSwitchover({
                    session_id: session.id,
                    afs_bond_switchover_enabled,
                  }),
                )
              }
            />
          </Field>
        )}
        <Field
          label="Pay coupons"
          hint="Pay out bond coupons to player wallets when due."
        >
          <Switch
            checked={session.pay_coupons}
            onChange={e => dispatch(togglePayCoupons({ pay_coupons: e }))}
          />
        </Field>
        {blueprintMode && (
          <Field
            label="News importance"
            hint='Comma-separated importance levels shown to players, e.g. "1,2,3".'
          >
            <Input
              style={{ width: 130 }}
              value={newsText}
              onChange={e => setNewsText(e.target.value)}
              onBlur={commitNewsImportances}
              onPressEnter={commitNewsImportances}
              aria-label="News importance levels"
            />
          </Field>
        )}
      </SettingsCard>
    </>
  );
};

export default GeneralCardV2;
